import * as z from "zod";
import type { ApiContext } from "../context";
import {
  type ApiNotAuthorized,
  CardIdentityTaken,
  CardNotFound,
  CardStateRefused,
  ErrorCode,
  InvalidFormField,
  StoreNotFound,
  type StoreRoleForbidden,
  UnexpectedApiError,
  ValidationError,
} from "../errors";
import {
  CardDetail,
  type CardEdit,
  CardEditResult,
  CardListItem,
  type CardListQuery,
  type CardPage,
  CardPricePoint,
  type CardStockUpdate,
  StockLevel,
} from "../model/card";
import { parse } from "../model/common";
import { forward, type Result } from "../result";
import type { Transport } from "../transport";
import { asRoleForbidden } from "./role";

const CardListResponse = z.object({ cards: z.array(CardListItem), nextCursor: z.string().nullable() });
const PriceHistoryResponse = z.object({ prices: z.array(CardPricePoint) });
const RepriceResponse = z.object({ priceCents: z.number().nullable() });
const SuccessResponse = z.object({ success: z.literal(true) });

/** `/shop/:shopId/card`: the shop's catalogue. */
export class CardsApi {
  constructor(private readonly t: Transport) {}

  async list(
    ctx: ApiContext,
    shopId: string,
    query: CardListQuery = {},
  ): Promise<Result<CardPage, ApiNotAuthorized | StoreNotFound>> {
    const r = await this.t.get(ctx, `/shop/${shopId}/card/list`, { query: { ...query } });
    return forward(
      r,
      (j) => parse(CardListResponse, j),
      (e) => {
        if (e instanceof ValidationError) {
          if (e.incorrectValue === "storeId") return new StoreNotFound(shopId, e);
          throw new UnexpectedApiError(e);
        }
        return e;
      },
    );
  }

  async get(
    ctx: ApiContext,
    shopId: string,
    cardId: string,
  ): Promise<Result<CardDetail, ApiNotAuthorized | StoreNotFound | CardNotFound>> {
    const r = await this.t.get(ctx, `/shop/${shopId}/card/${cardId}`);
    return forward(
      r,
      (j) => parse(CardDetail, j),
      (e) => {
        if (e instanceof ValidationError) {
          if (e.incorrectValue === "storeId") return new StoreNotFound(shopId, e);
          if (e.incorrectValue === "") return new CardNotFound(cardId, e);
          throw new UnexpectedApiError(e);
        }
        return e;
      },
    );
  }

  /** Looks the market up again. Null when the source had nothing, in which case the stored price is left alone. */
  async reprice(
    ctx: ApiContext,
    shopId: string,
    cardId: string,
  ): Promise<Result<number | null, ApiNotAuthorized | StoreNotFound | CardNotFound | StoreRoleForbidden>> {
    const r = await this.t.post(ctx, `/shop/${shopId}/card/${cardId}/reprice`);
    return forward(
      r,
      (j) => parse(RepriceResponse, j).priceCents,
      (e) => {
        if (e instanceof ValidationError) {
          const forbidden = asRoleForbidden(e);
          if (forbidden) return forbidden;
          if (e.incorrectValue === "storeId") return new StoreNotFound(shopId, e);
          if (e.incorrectValue === "cardId") return new CardNotFound(cardId, e);
          throw new UnexpectedApiError(e);
        }
        return e;
      },
    );
  }

  /** Every price ever recorded for the card, oldest first: market lookups, what the shop marked, floors. */
  async prices(
    ctx: ApiContext,
    shopId: string,
    cardId: string,
  ): Promise<Result<CardPricePoint[], ApiNotAuthorized | StoreNotFound>> {
    const r = await this.t.get(ctx, `/shop/${shopId}/card/${cardId}/prices`);
    return forward(
      r,
      (j) => parse(PriceHistoryResponse, j).prices,
      (e) => {
        if (e instanceof ValidationError) {
          if (e.incorrectValue === "storeId") return new StoreNotFound(shopId, e);
          throw new UnexpectedApiError(e);
        }
        return e;
      },
    );
  }

  /** Retires a card: it stays for what points at it and stops being listed or quoted. Needs zero on hand and no open cart. */
  async archive(
    ctx: ApiContext,
    shopId: string,
    cardId: string,
  ): Promise<Result<void, ApiNotAuthorized | StoreNotFound | CardNotFound | CardStateRefused | StoreRoleForbidden>> {
    const r = await this.t.post(ctx, `/shop/${shopId}/card/${cardId}/archive`);
    return forward(
      r,
      (j) => {
        parse(SuccessResponse, j);
      },
      (e) => {
        if (e instanceof ValidationError) {
          const forbidden = asRoleForbidden(e);
          if (forbidden) return forbidden;
          if (e.incorrectValue === "storeId") return new StoreNotFound(shopId, e);
          if (e.incorrectValue === "cardId" && e.code === ErrorCode.DoesntExist) return new CardNotFound(cardId, e);
          if (e.incorrectValue === "cardId") return new CardStateRefused(e.detailedDesc, e);
          throw new UnexpectedApiError(e);
        }
        return e;
      },
    );
  }

  /**
   * Corrects what a card is. Refused for an archived card, and — when the edit
   * makes it a different card — for one that was sold, sits in an open cart,
   * or whose new identity another card already has.
   */
  async edit(
    ctx: ApiContext,
    shopId: string,
    cardId: string,
    edit: CardEdit,
  ): Promise<
    Result<
      CardEditResult,
      | ApiNotAuthorized
      | StoreNotFound
      | CardNotFound
      | CardStateRefused
      | CardIdentityTaken
      | StoreRoleForbidden
      | InvalidFormField
    >
  > {
    const r = await this.t.patch(ctx, `/shop/${shopId}/card/${cardId}`, { body: edit });
    return forward(
      r,
      (j) => parse(CardEditResult, j),
      (e) => {
        if (e instanceof ValidationError) {
          const forbidden = asRoleForbidden(e);
          if (forbidden) return forbidden;
          if (e.incorrectValue === "storeId") return new StoreNotFound(shopId, e);
          if (e.incorrectValue === "cardId" && e.code === ErrorCode.DoesntExist) return new CardNotFound(cardId, e);
          if (e.incorrectValue === "cardId") return new CardStateRefused(e.detailedDesc, e);
          if (e.incorrectValue === "identity") return new CardIdentityTaken(e.detailedDesc, e);
          return new InvalidFormField(e.incorrectValue, e.detailedDesc, e);
        }
        return e;
      },
    );
  }

  /** Sets the total on hand, or stops counting. Returns what moved and the new total. */
  async setStock(
    ctx: ApiContext,
    shopId: string,
    cardId: string,
    update: CardStockUpdate,
  ): Promise<
    Result<
      StockLevel,
      ApiNotAuthorized | StoreNotFound | CardNotFound | CardStateRefused | StoreRoleForbidden | InvalidFormField
    >
  > {
    const r = await this.t.post(ctx, `/shop/${shopId}/card/${cardId}/stock`, { body: update });
    return forward(
      r,
      (j) => parse(StockLevel, j),
      (e) => {
        if (e instanceof ValidationError) {
          const forbidden = asRoleForbidden(e);
          if (forbidden) return forbidden;
          if (e.incorrectValue === "storeId") return new StoreNotFound(shopId, e);
          if (e.incorrectValue === "cardId" && e.code === ErrorCode.DoesntExist) return new CardNotFound(cardId, e);
          if (e.incorrectValue === "cardId") return new CardStateRefused(e.detailedDesc, e);
          return new InvalidFormField(e.incorrectValue, e.detailedDesc, e);
        }
        return e;
      },
    );
  }

  /** What the shop asks, in cents. Null takes the card off sale. */
  async setPrice(
    ctx: ApiContext,
    shopId: string,
    cardId: string,
    priceCents: number | null,
  ): Promise<Result<void, ApiNotAuthorized | StoreNotFound | CardNotFound | StoreRoleForbidden | InvalidFormField>> {
    const r = await this.t.post(ctx, `/shop/${shopId}/card/${cardId}/price`, { body: { priceCents } });
    return forward(
      r,
      (j) => {
        parse(SuccessResponse, j);
      },
      (e) => {
        if (e instanceof ValidationError) {
          const forbidden = asRoleForbidden(e);
          if (forbidden) return forbidden;
          if (e.incorrectValue === "storeId") return new StoreNotFound(shopId, e);
          if (e.incorrectValue === "cardId") return new CardNotFound(cardId, e);
          return new InvalidFormField(e.incorrectValue, e.detailedDesc, e);
        }
        return e;
      },
    );
  }

  /** Below this the shop does not sell. Null clears the floor, which sends every quote to review. */
  async setFloor(
    ctx: ApiContext,
    shopId: string,
    cardId: string,
    floorPriceCents: number | null,
  ): Promise<Result<void, ApiNotAuthorized | StoreNotFound | CardNotFound | StoreRoleForbidden | InvalidFormField>> {
    const r = await this.t.post(ctx, `/shop/${shopId}/card/${cardId}/floor`, { body: { floorPriceCents } });
    return forward(
      r,
      (j) => {
        parse(SuccessResponse, j);
      },
      (e) => {
        if (e instanceof ValidationError) {
          const forbidden = asRoleForbidden(e);
          if (forbidden) return forbidden;
          if (e.incorrectValue === "storeId") return new StoreNotFound(shopId, e);
          if (e.incorrectValue === "cardId") return new CardNotFound(cardId, e);
          return new InvalidFormField(e.incorrectValue, e.detailedDesc, e);
        }
        return e;
      },
    );
  }
}
